import {
  autoUpdate,
  flip,
  FloatingFocusManager,
  FloatingPortal,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
} from "@floating-ui/react";
import { useState } from "react";
import { useTranslations } from "use-intl";

import { ExpandIcon, PriceCheckIcon } from "@/components/appIcons";
import { Price } from "@/components/Price";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

type PriceRange = { priceMin?: number; priceMax?: number };

function parsePrice(value: string) {
  if (!value.trim()) return undefined;
  if (!/^\d+(?:[.,]\d{1,2})?$/.test(value.trim())) return NaN;
  return Number(value.trim().replace(",", "."));
}

function rangeKind({ priceMin, priceMax }: PriceRange) {
  if (priceMin !== undefined && priceMax !== undefined) return "range";
  if (priceMin !== undefined) return "from";
  if (priceMax !== undefined) return "to";
  return "none";
}

function PriceRangeForm({
  priceMin,
  priceMax,
  currency,
  onApply,
}: PriceRange & { currency: string; onApply: (range: PriceRange) => void }) {
  const t = useTranslations("facets");
  const [minimum, setMinimum] = useState(String(priceMin ?? ""));
  const [maximum, setMaximum] = useState(String(priceMax ?? ""));
  const [invalid, setInvalid] = useState(false);
  return (
    <form
      className="space-y-4 p-4"
      onSubmit={(event) => {
        event.preventDefault();
        const min = parsePrice(minimum);
        const max = parsePrice(maximum);
        if (
          Number.isNaN(min) ||
          Number.isNaN(max) ||
          (min !== undefined && max !== undefined && min > max)
        ) {
          setInvalid(true);
          return;
        }
        onApply({ priceMin: min, priceMax: max });
      }}
    >
      <div className="grid grid-cols-2 gap-3">
        <label className="space-y-1.5 text-sm font-medium">
          <span>{t("priceMinimum", { currency })}</span>
          <Input
            inputMode="decimal"
            enterKeyHint="next"
            autoComplete="off"
            value={minimum}
            onChange={(event) => {
              setMinimum(event.target.value);
              setInvalid(false);
            }}
            onFocus={(event) => event.target.select()}
            placeholder="0"
            className="text-base"
            aria-invalid={invalid}
          />
        </label>
        <label className="space-y-1.5 text-sm font-medium">
          <span>{t("priceMaximum", { currency })}</span>
          <Input
            inputMode="decimal"
            enterKeyHint="done"
            autoComplete="off"
            value={maximum}
            onChange={(event) => {
              setMaximum(event.target.value);
              setInvalid(false);
            }}
            onFocus={(event) => event.target.select()}
            placeholder={t("priceNoLimit")}
            className="text-base"
            aria-invalid={invalid}
          />
        </label>
      </div>
      {invalid && (
        <p role="alert" className="text-sm text-destructive">
          {t("priceInvalidRange")}
        </p>
      )}
      <div className="flex items-center justify-between gap-3">
        <Button type="button" variant="ghost" onClick={() => onApply({})}>
          {t("clear")}
        </Button>
        <Button type="submit">{t("applyFilters")}</Button>
      </div>
    </form>
  );
}

export function OfferPriceFilter({
  priceMin,
  priceMax,
  currency,
  onChange,
}: PriceRange & {
  currency: string;
  onChange: (range: PriceRange) => void;
}) {
  const t = useTranslations("facets");
  const [open, setOpen] = useState(false);
  const { refs, floatingStyles, context } = useFloating({
    open,
    onOpenChange: setOpen,
    placement: "bottom-end",
    whileElementsMounted: autoUpdate,
    middleware: [offset(6), flip(), shift({ padding: 12 })],
  });
  const click = useClick(context);
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: "dialog" });
  const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss, role]);
  const kind = rangeKind({ priceMin, priceMax });
  return (
    <>
      <button
        type="button"
        ref={refs.setReference}
        {...getReferenceProps()}
        className={cn(
          "flex h-11 w-full min-w-0 items-center gap-1.5 rounded-md border px-2.5 text-sm transition-colors focus-visible:ring-2 focus-visible:ring-primary sm:h-10",
          kind === "none"
            ? "border-border bg-secondary/50 text-muted-foreground hover:text-foreground"
            : "border-primary/30 bg-primary/10 text-foreground",
        )}
      >
        <PriceCheckIcon decorative className="shrink-0 text-[18px]" />
        <span className="min-w-0 flex-1 truncate text-left">
          {t.rich("priceRangeSummary", {
            kind,
            minimum: () => <Price amount={priceMin} currency={currency} plain />,
            maximum: () => <Price amount={priceMax} currency={currency} plain />,
          })}
        </span>
        <ExpandIcon decorative className="shrink-0 text-base" />
      </button>
      {open && (
        <FloatingPortal>
          <FloatingFocusManager context={context} modal={false} returnFocus>
            <div
              ref={refs.setFloating}
              style={floatingStyles}
              {...getFloatingProps()}
              aria-label={t("price")}
              className="z-[70] w-80 max-w-[calc(100vw-1.5rem)] rounded-lg border border-border bg-popover text-popover-foreground shadow-lg"
            >
              <PriceRangeForm
                priceMin={priceMin}
                priceMax={priceMax}
                currency={currency}
                onApply={(range) => {
                  onChange(range);
                  setOpen(false);
                }}
              />
            </div>
          </FloatingFocusManager>
        </FloatingPortal>
      )}
    </>
  );
}
